import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  KeyIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';

const SCOSettings = () => {
  const [settings, setSettings] = useState({
    sco_enabled: false,
    sco_endpoint: '',
    sco_api_key: '',
    sco_timeout: 30,
    sco_retry_attempts: 3,
    sco_validation_mode: 'strict',
    sco_auto_submit: false,
    sco_backup_enabled: true,
    sco_log_level: 'info'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    loadSCOSettings();
  }, []);

  const loadSCOSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/sco-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error loading SCO settings:', error);
      toast.error('Failed to load SCO settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await apiClient.post('/admin/sco-settings', settings);
      toast.success('SCO settings saved successfully');
    } catch (error) {
      console.error('Error saving SCO settings:', error);
      toast.error('Failed to save SCO settings');
    } finally {
      setSaving(false);
    }
  };

  const testSCOConnection = async () => {
    try {
      setTestingConnection(true);
      setConnectionStatus(null);
      
      const response = await apiClient.post('/admin/sco-settings/test', {
        endpoint: settings.sco_endpoint,
        api_key: settings.sco_api_key,
        timeout: settings.sco_timeout
      });
      
      if (response.data.success) {
        setConnectionStatus({ type: 'success', message: 'SCO connection successful' });
        toast.success('SCO connection test passed');
      } else {
        setConnectionStatus({ type: 'error', message: response.data.message || 'Connection failed' });
        toast.error('SCO connection test failed');
      }
    } catch (error) {
      console.error('Error testing SCO connection:', error);
      setConnectionStatus({ type: 'error', message: error.response?.data?.message || 'Connection test failed' });
      toast.error('SCO connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="border-b border-gray-200 pb-3">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <CogIcon className="h-8 w-8 mr-3 text-blue-600" />
          SCO Settings
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Configure SCO (System Configuration Options) integration settings
        </p>
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">📋 How to Configure SCO:</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li><strong>Endpoint URL:</strong> Get from your SCO service provider (e.g., https://api.sco-provider.com/v1/validate)</li>
            <li><strong>API Key:</strong> Obtain by registering with your SCO service provider</li>
            <li><strong>Test Mode:</strong> Use test values for demo: endpoint "https://api.example-sco.com/v1/validate", key "test_key_12345"</li>
          </ul>
        </div>
      </div>

      {/* Connection Status */}
      {connectionStatus && (
        <div className={`rounded-md p-4 ${
          connectionStatus.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            {connectionStatus.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                connectionStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {connectionStatus.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Configuration */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2 text-blue-600" />
            Basic Configuration
          </h3>
          
          <div className="space-y-4">
            {/* SCO Enabled */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.sco_enabled}
                  onChange={(e) => handleSettingChange('sco_enabled', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Enable SCO Integration</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Enable or disable SCO system integration
              </p>
            </div>

            {/* SCO Endpoint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SCO Endpoint URL
              </label>
              <input
                type="url"
                value={settings.sco_endpoint}
                onChange={(e) => handleSettingChange('sco_endpoint', e.target.value)}
                placeholder="https://sco-api.example.com/v1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.sco_enabled}
              />
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SCO API Key
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={settings.sco_api_key}
                  onChange={(e) => handleSettingChange('sco_api_key', e.target.value)}
                  placeholder="Enter SCO API key"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={!settings.sco_enabled}
                />
                <KeyIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {/* Test Connection Button */}
            <button
              onClick={testSCOConnection}
              disabled={!settings.sco_enabled || !settings.sco_endpoint || !settings.sco_api_key || testingConnection}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testingConnection ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing Connection...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Test SCO Connection
                </>
              )}
            </button>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CogIcon className="h-5 w-5 mr-2 text-blue-600" />
            Advanced Settings
          </h3>
          
          <div className="space-y-4">
            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Connection Timeout (seconds)
              </label>
              <input
                type="number"
                min="5"
                max="300"
                value={settings.sco_timeout}
                onChange={(e) => handleSettingChange('sco_timeout', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.sco_enabled}
              />
            </div>

            {/* Retry Attempts */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Retry Attempts
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.sco_retry_attempts}
                onChange={(e) => handleSettingChange('sco_retry_attempts', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.sco_enabled}
              />
            </div>

            {/* Validation Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validation Mode
              </label>
              <select
                value={settings.sco_validation_mode}
                onChange={(e) => handleSettingChange('sco_validation_mode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.sco_enabled}
              >
                <option value="strict">Strict</option>
                <option value="moderate">Moderate</option>
                <option value="lenient">Lenient</option>
              </select>
            </div>

            {/* Auto Submit */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.sco_auto_submit}
                  onChange={(e) => handleSettingChange('sco_auto_submit', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  disabled={!settings.sco_enabled}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Auto Submit to SCO</span>
              </label>
            </div>

            {/* Backup Enabled */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.sco_backup_enabled}
                  onChange={(e) => handleSettingChange('sco_backup_enabled', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  disabled={!settings.sco_enabled}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Enable Backup</span>
              </label>
            </div>

            {/* Log Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Log Level
              </label>
              <select
                value={settings.sco_log_level}
                onChange={(e) => handleSettingChange('sco_log_level', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.sco_enabled}
              >
                <option value="error">Error</option>
                <option value="warn">Warning</option>
                <option value="info">Info</option>
                <option value="debug">Debug</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">About SCO Integration</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                SCO (System Configuration Options) integration allows your system to communicate with external 
                configuration management services. This enables centralized configuration management, 
                automated settings synchronization, and enhanced system monitoring capabilities.
              </p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>Centralized configuration management</li>
                <li>Real-time settings synchronization</li>
                <li>Enhanced monitoring and logging</li>
                <li>Automated backup and recovery</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Save SCO Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SCOSettings;